import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Box, useTheme } from '@mui/material';
import { IconPlus } from '@tabler/icons-react';

import { Button, SearchBar, Typography } from '@zendesk/forethought-elements';
import { HorizontalStackedBar } from '../workflow-builder/intent-workflows-table/HorizontalStackedBar';
import AbTestMoreMenu from './AbTestMoreMenu';
import AbTestSummaryCards from './AbTestSummaryCards';
import CreateOrRenameAbTestDialog from './CreateOrRenameAbTestDialog';
import StopAbTestConfirmModal from './StopAbTestConfirmModal';
import TrafficSplitTooltipContent from './TrafficSplitTooltipContent';
import CardTable from 'src/components/card-table/CardTable';
import {
  DarkPageLayout,
  DarkPageTableContainer,
} from 'src/components/dark-page-layout';
import HighlightedText from 'src/components/highlighted-text';
import Spinner from 'src/components/spinner';
import TableOverflowCell from 'src/components/table-overflow-cell/TableOverflowCell';
import { NA } from 'src/constants/solve';
import { useListAbTestConfigsSummaryQuery } from 'src/services/agent-ab-test/agentAbTestApi';
import {
  SolveABTestConfigSummary,
  SolveABTestStatus,
} from 'src/services/agent-ab-test/types';
import { Routes } from 'src/utils/enums';
import { formatProductLabel } from 'src/utils/formatProductChannels';

const AgentAbTestPage = () => {
  const theme = useTheme();
  const { t } = useTranslation('agent-ab-test');

  const STATUS_BADGE_LABELS: Record<SolveABTestStatus, string> = useMemo(
    () => ({
      canceled: t('status.canceled'),
      completed: t('status.completed'),
      draft: t('status.notStarted'),
      running: t('status.running'),
    }),
    [t],
  );
  const [searchText, setSearchText] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading } = useListAbTestConfigsSummaryQuery({
    // TODO: implement pagination
    page: 1,
    pageSize: 5000,
  });

  const [stoppingAbTestConfig, setStoppingAbTestConfig] =
    useState<SolveABTestConfigSummary>();
  const cancelStop = useCallback(() => {
    setStoppingAbTestConfig(undefined);
  }, []);

  const columns = useMemo(
    () => [
      {
        columnWidth: 180,
        id: 'name',
        label: t('columns.name'),
        renderCell: (row: SolveABTestConfigSummary) => (
          <TableOverflowCell clamp={2}>
            <Typography variant='font14'>
              <HighlightedText keyword={searchText} text={row.name} />
            </Typography>
          </TableOverflowCell>
        ),
      },
      {
        columnWidth: 100,
        id: 'status',
        label: t('columns.status'),
        renderCell: (row: SolveABTestConfigSummary) => (
          <Box
            sx={t => ({
              backgroundColor: {
                canceled: t.palette.colors.red[500],
                completed: t.palette.colors.blue[500],
                draft: t.palette.colors.grey[500],
                running: t.palette.colors.green[500],
              }[row.status],
              borderRadius: '4px',
              display: 'inline-flex',
              padding: '4px 8px',
            })}
          >
            <Typography color={theme.palette.colors.white} variant='font11'>
              {STATUS_BADGE_LABELS[row.status]}
            </Typography>
          </Box>
        ),
      },
      {
        columnWidth: 80,
        id: 'channel',
        label: t('columns.channel'),
        renderCell: (row: SolveABTestConfigSummary) => (
          <TableOverflowCell clamp={1}>
            <Typography variant='font14'>
              {row.product ? formatProductLabel(row.product) : ''}
            </Typography>
          </TableOverflowCell>
        ),
      },
      {
        columnWidth: 140,
        id: 'agent',
        label: t('columns.agent'),
        renderCell: (row: SolveABTestConfigSummary) => (
          <TableOverflowCell clamp={2}>
            <Typography variant='font14'>{row.intent_name ?? ''}</Typography>
          </TableOverflowCell>
        ),
      },
      {
        columnWidth: 260,
        id: 'description',
        label: t('columns.description'),
        renderCell: (row: SolveABTestConfigSummary) => (
          <TableOverflowCell clamp={2}>
            <Typography variant='font14'>{row.description ?? ''}</Typography>
          </TableOverflowCell>
        ),
      },
      {
        columnWidth: 140,
        id: 'traffic_split',
        label: t('columns.trafficSplit'),
        renderCell: (row: SolveABTestConfigSummary) => {
          const barData =
            row.variant_summary?.map((v, index) => ({
              color: theme.palette.chart[index],
              value: v.traffic_percentage,
            })) || [];

          if (!barData.length) {
            return (
              <Typography
                color={theme.palette.colors.grey[600]}
                variant='font14'
              >
                {NA}
              </Typography>
            );
          }

          return (
            <HorizontalStackedBar
              data={barData}
              suffix='%'
              tooltipContent={
                <TrafficSplitTooltipContent abTestConfigSummary={row} />
              }
              tooltipPopperProps={{
                sx: {
                  '& .MuiTooltip-arrow': {
                    color: theme.palette.colors.white,
                  },
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: theme.palette.colors.white,
                    border: `1px solid ${theme.palette.colors.slate[200]}`,
                    boxShadow:
                      '0px 0px 1px rgba(0, 12, 32, 0.04), 0px 2px 6px rgba(3, 17, 38, 0.11)',
                  },
                  '& .MuiTooltip-tooltip .MuiTypography-root': {
                    color: theme.palette.colors.black,
                  },
                },
              }}
            />
          );
        },
      },
      {
        columnWidth: 50,
        fixedWidth: true,
        id: 'actions',
        label: '',
        renderCell: (row: SolveABTestConfigSummary) => (
          <Box
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <AbTestMoreMenu
              abTest={row}
              onStopClick={() => setStoppingAbTestConfig(row)}
              showViewResultsOption
            />
          </Box>
        ),
      },
    ],
    [
      STATUS_BADGE_LABELS,
      searchText,
      t,
      theme.palette.chart,
      theme.palette.colors.black,
      theme.palette.colors.grey,
      theme.palette.colors.slate,
      theme.palette.colors.white,
    ],
  );

  // cellPadding='12px 16px' → 32px per flex col; CardTable hardcodes 8px/side for fixed cols.
  const tableMinWidth = useMemo(
    () =>
      columns.reduce(
        (sum, col) =>
          sum + (col.columnWidth ?? 100) + (col.fixedWidth ? 16 : 32),
        0,
      ),
    [columns],
  );

  const rows = useMemo(() => {
    const configs = data?.ab_test_configs ?? [];
    if (!searchText) {
      return configs;
    }
    return configs.filter(item =>
      item.name.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [data?.ab_test_configs, searchText]);

  const handleCreateNew = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  const toolbar = (
    <Box
      sx={{
        display: 'flex',
        gap: '16px',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ width: '300px' }}>
        <SearchBar
          onChange={event => setSearchText(event.target.value)}
          onClear={() => setSearchText('')}
          placeholder={t('toolbar.searchPlaceholder')}
          size='small'
          value={searchText}
        />
      </Box>
      <Button
        onClick={handleCreateNew}
        startIcon={<IconPlus size={20} strokeWidth={1.5} />}
      >
        {t('toolbar.createNew')}
      </Button>
    </Box>
  );

  return (
    <DarkPageLayout summaryCards={<AbTestSummaryCards />}>
      <CreateOrRenameAbTestDialog
        onClose={() => setIsCreateDialogOpen(false)}
        open={isCreateDialogOpen}
      />
      {stoppingAbTestConfig && (
        <StopAbTestConfirmModal
          abTestConfig={stoppingAbTestConfig}
          onClose={cancelStop}
        />
      )}
      <DarkPageTableContainer tableOverflowY='hidden' toolbar={toolbar}>
        {isLoading ? (
          <Spinner />
        ) : (
          <CardTable
            cellPadding='12px 16px'
            columns={columns}
            headerColor={theme.palette.colors.grey[600]}
            headerVariant='font14'
            hoverBorderColor={theme.palette.colors.teal[300]}
            itemSize={74}
            minWidth={tableMinWidth}
            onClickRow={row => {
              navigate(
                `${Routes.AGENT_AB_TESTS}/${encodeURIComponent(
                  row.ab_test_config_id,
                )}`,
              );
            }}
            rows={rows}
            showHeaderBorder={false}
            spacing={8}
          />
        )}
      </DarkPageTableContainer>
    </DarkPageLayout>
  );
};

export default AgentAbTestPage;
